import axios from 'axios'
import environmentConfig from '../config/environmentConfig'
import store from '@/store'
import { getBrandConfig } from '../brand'

const VODACOM_REGEX = /^(27|0)?(82|83|84)\d{7}$/
const DEFAULT_HEADERS = Object.freeze({ Authorization: `Basic ${environmentConfig.authHeader}` })
const UNSUBSCRIBE_HEADERS = Object.freeze({ Authorization: `Basic ${environmentConfig.authHeaderUnsubscribe}` })

function parseApiError(error, fallbackMessage) {
  const data = error.response?.data || {}
  const reason = data.reason || data.message || data.error
  const errorMessage = reason
    ? (reason.includes('[') ? reason : `${fallbackMessage} due to [${reason}]`)
    : fallbackMessage
  return { success: false, error: errorMessage, reason: data.reason || reason }
}

function checkResultFailure(data, fallbackMessage) {
  if (data.result === false || data.success === false) {
    const reason = data.reason || data.status || data.message || data.error || fallbackMessage
    return { success: false, error: reason, reason: data.reason, status: data.status }
  }
  return null
}

function getBrandValue(key) {
  const config = store?.state?.brandConfig || (typeof getBrandConfig === 'function' ? getBrandConfig() : null)
  if (!config || config[key] == null) {
    throw new Error(`Brand config missing required value: ${key}`)
  }
  return config[key]
}

export default class DcbService {
  static getProductId() {
    return getBrandValue('productId')
  }

  static getExternalRef() {
    return getBrandValue('externalRef')
  }

  static getRevenuePartnerId() {
    return getBrandValue('revenuePartnerId')
  }

  static formatMsisdn(phone) {
    let cleaned = phone.replace(/\s/g, '').replace(/[^0-9]/g, '')
    if (cleaned.startsWith('0')) cleaned = '27' + cleaned.substring(1)
    if (!cleaned.startsWith('27')) cleaned = '27' + cleaned
    return cleaned
  }

  static isVodacomNumber(phone) {
    return VODACOM_REGEX.test(phone.replace(/\s/g, '').replace(/[^0-9]/g, ''))
  }

  /**
   * Encrypt MSISDN (plain number → encrypted string).
   * Use before redirecting to voucher store etc.: ?e-msisdn=${encryptedMsisdn}&productId=26
   * @param {string} msisdn - Plain MSISDN (e.g. '27812345678')
   * @returns {{ success: boolean, encryptedMsisdn?: string, error?: string }}
   */
  static async encryptUserMsisdn(msisdn) {
    try {
      const response = await axios.post(
        `${environmentConfig.dcbService}/encrypt-msisdn`,
        { msisdn: this.formatMsisdn(msisdn || store?.state?.msisdn || '') },
        { headers: DEFAULT_HEADERS }
      )
      const data = response?.data || {}
      return { success: true, encryptedMsisdn: data.encryptedMsisdn, ...data }
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Encryption failed' }
    }
  }

  /**
   * Decrypt MSISDN (encrypted string → plain number).
   * Use when receiving e-msisdn from URL query params.
   * @param {string} encryptedMsisdn - Encrypted MSISDN string
   * @returns {{ success: boolean, rawMsisdn?: string, error?: string }}
   */
  static async decryptMsisdn(encryptedMsisdn) {
    try {
      const response = await axios.post(
        `${environmentConfig.dcbService}/decrypt-msisdn`,
        { encryptedMsisdn },
        { headers: DEFAULT_HEADERS }
      )
      const data = response?.data || {}
      return { success: true, rawMsisdn: data.rawMsisdn, ...data }
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Decryption failed' }
    }
  }

  static async requestOtp(msisdn, serviceId) {
    try {
      const response = await axios.post(
        `${environmentConfig.dcbService}/request-otp`,
        { msisdn: this.formatMsisdn(msisdn), serviceId },
        { headers: DEFAULT_HEADERS }
      )
      return { success: true, ...response.data }
    } catch {
      return { success: false, error: 'network', message: 'Failed to send OTP' }
    }
  }

  static async validateOtp(msisdn, serviceId, otp) {
    try {
      const response = await axios.post(
        `${environmentConfig.dcbService}/validate-otp`,
        { msisdn: this.formatMsisdn(msisdn), serviceId, otp },
        { headers: DEFAULT_HEADERS }
      )
      const { data } = response

      if (data.sessionId) return { success: true, ...data }
      if (data.OTPAttemptCount > 2) return { success: false, error: 'You have exceeded your OTP attempt limit' }
      if (data.UsedOTP) return { success: false, error: 'This OTP has already been used' }
      if (data.ExpiredOTP) return { success: false, error: 'This OTP has expired. Click Resend OTP' }
      return { success: false, error: 'Invalid OTP, please re-enter the OTP' }
    } catch {
      return { success: false, error: 'Verification failed' }
    }
  }

  static async getUserViaProductId(msisdn, productIdOverride = null) {
    const formatted = this.formatMsisdn(
      msisdn || store?.state?.msisdn || localStorage.getItem('msisdn') || ''
    )
    const productId = productIdOverride ?? this.getProductId()

    try {
      const response = await axios.post(
        `${environmentConfig.dcbService}/get-user-via-product-id`,
        { msisdn: formatted, productId, externalRef: this.getExternalRef() },
        { headers: DEFAULT_HEADERS }
      )
      return { success: true, ...response.data }
    } catch {
      return { success: false, authError: true }
    }
  }

  static async subscribeUser(msisdn, serviceId, campaignId = 0, clickId = null, googleClickId = null) {
    try {
      const response = await axios.post(
        `${environmentConfig.dcbService}/subscribe-sports-hub`,
        {
          msisdn: this.formatMsisdn(msisdn),
          productId: this.getProductId(),
          serviceId,
          externalRef: this.getExternalRef(),
          campaignId,
          revenuePartnerId: this.getRevenuePartnerId(),
          acquisitionChannel: 'Organic-Web',
          googleClickId: googleClickId ?? null,
          clickId: clickId ?? null,
          channel: 'USSD',
          packageType: 'daily'
        },
        { headers: DEFAULT_HEADERS }
      )
      const data = response?.data || {}
      const failure = checkResultFailure(data, 'Subscription failed')
      return failure || { success: true, ...data }
    } catch (error) {
      return parseApiError(error, 'Subscription failed')
    }
  }

  static async unsubscribeUser(msisdn, serviceId, sessionId, isDcbOnly = false) {
    try {
      const response = await axios.post(
        `${environmentConfig.dcbService}/unsubscribe`,
        {
          msisdn: this.formatMsisdn(msisdn),
          serviceId,
          externalRef: this.getExternalRef(),
          sessionId,
          isDcbOnly
        },
        { headers: UNSUBSCRIBE_HEADERS }
      )
      const data = response?.data || {}
      const failure = checkResultFailure(data, 'Unsubscribe failed')
      return failure || { success: true, ...data }
    } catch (error) {
      return parseApiError(error, 'Unsubscribe failed')
    }
  }

  static async createSessionId(msisdn, serviceId) {
    try {
      const response = await axios.post(
        `${environmentConfig.dcbService}/create-session-id`,
        { msisdn: this.formatMsisdn(msisdn), serviceId, externalRef: this.getExternalRef() },
        { headers: DEFAULT_HEADERS }
      )
      return response.data
    } catch {
      return { authError: true }
    }
  }

  static async trackLogin(msisdn, service) {
    try {
      return await axios.post(
        `${environmentConfig.dcbService}/track-login`,
        { msisdn: this.formatMsisdn(msisdn), service, isWeb: 1 },
        { headers: DEFAULT_HEADERS }
      )
    } catch {
      return { authError: true }
    }
  }
}
